import { useNavigate } from 'react-router-dom'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { CircleHelp, Eye, Lock, Sparkles, Trash2 } from 'lucide-react'
import { Button } from '@/shared/ui'
import { usePreviewClubName } from '../hooks/use-preview-club-name'
import { usePreviewSession } from '../hooks/use-preview-session'
import { usePreviewWelcome } from '../hooks/use-preview-welcome'

const PREVIEW_WINDOW_DAYS = 15
const TRIAL_DAYS = 90

/**
 * One-time explainer shown right after `redeem_preview_token` signs the
 * prospect in. This is the first impression a Granada partner-club owner
 * gets of their own club inside uPadelMatch — so the priorities here are:
 *
 *  1. Greet them by club name (makes the "this is your club" framing land).
 *  2. Reassure them the data is fake and the real-data fear is moot.
 *  3. Be explicit about the three states (read-only now / claim to start
 *     trial / auto-cleanup if abandoned) in card-style sections so the
 *     scan-and-go reader gets each beat without reading prose.
 *  4. Make the primary CTA copy unambiguous — "Reclamar mi club" beats
 *     "Reclamar club" because possessive language reduces the "wait, is
 *     this really mine?" hesitation.
 *
 * Mounted alongside `PreviewBanner` in `AppShell`. Renders nothing for
 * non-preview sessions or once the user has dismissed for this token.
 */
export function PreviewWelcomeDialog() {
  const navigate = useNavigate()
  const session = usePreviewSession()
  const welcome = usePreviewWelcome()
  // Club name read from the same `clubs/{clubId}` doc the rest of the
  // app uses. Rules already permit preview sessions to read their own
  // club, so the query resolves under the visitor's custom claims. The
  // settings/hooks → preview-session/components crosscut is forbidden
  // by the boundaries rule, so the lookup goes through a sibling hook.
  const clubName = usePreviewClubName()

  if (!session.active || !welcome.shouldShow) return null

  if (session.isPublicDemo) {
    return (
      <AlertDialog.Root open onOpenChange={(next) => { if (!next) welcome.dismiss() }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="modal-scrim" />
          <AlertDialog.Content className="modal preview-welcome" style={{ width: 'min(clamp(420px, 48vw, 720px), 92vw)' }}>
            <div className="modal-head">
              <AlertDialog.Title>Explora Padel Demo</AlertDialog.Title>
            </div>
            <div className="modal-body">
              <AlertDialog.Description>
                Esta demostración usa datos ficticios que se actualizan cada semana.
                Puedes recorrer el panel en modo lectura, sin registrarte.
              </AlertDialog.Description>
              <p>Si te gusta, crea tu propio club en minutos y empieza 90 días gratis, sin tarjeta.</p>
            </div>
            <div className="modal-foot">
              <AlertDialog.Cancel asChild>
                <Button variant="ghost" size="sm" icon={Eye}>Explorar demo</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <Button
                  variant="primary"
                  size="sm"
                  icon={Sparkles}
                  className="preview-allowed"
                  onClick={() => window.open('https://upadelmatch.com/clubes/#contact-form', '_blank', 'noopener,noreferrer')}
                >
                  Crear mi club
                </Button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    )
  }

  const goToClaim = () => {
    welcome.dismiss()
    navigate('/settings?tab=subscription')
  }

  return (
    <AlertDialog.Root
      open
      onOpenChange={(next) => {
        if (!next) welcome.dismiss()
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="modal-scrim" />
        {/* The `min(…, 92vw)` cap keeps the dialog inside phone viewports —
            the 420px clamp floor alone overflows screens < 420px (iPhone 8 =
            375px), clipping the first-impression text. On any screen ≥ ~782px
            the clamp value is always < 92vw, so desktop/tablet is unchanged. */}
        <AlertDialog.Content className="modal preview-welcome" style={{ width: 'min(clamp(420px, 48vw, 720px), 92vw)' }}>
          <div className="modal-head">
            <AlertDialog.Title asChild>
              <h2>
                <span aria-hidden="true">🎾</span>{' '}
                {clubName ? `Bienvenido a ${clubName}` : 'Bienvenido a tu club'}
              </h2>
            </AlertDialog.Title>
          </div>
          <div className="modal-body">
            <div className="preview-welcome-greeting">
              <h3>Modo demostración</h3>
              <AlertDialog.Description asChild>
                <p>
                  Hemos preparado tu zona de pruebas con datos de ejemplo —
                  jugadores, reservas e ingresos típicos de un club como el
                  tuyo — para que veas la app en marcha. Echa un vistazo a
                  cada pantalla; cuando estés listo, reclama el club para
                  empezar tu prueba gratis.
                </p>
              </AlertDialog.Description>
            </div>

            <ul className="preview-welcome-cards">
              <li className="preview-welcome-card">
                <div className="preview-welcome-card-icon" aria-hidden="true">
                  <Lock size={18} />
                </div>
                <div className="preview-welcome-card-body">
                  <h4>Tu zona de pruebas</h4>
                  <p>
                    Explora cada pantalla sin compromiso. De momento las
                    acciones están bloqueadas mientras te enseñamos el
                    funcionamiento; al reclamar el club activamos todo.
                  </p>
                </div>
              </li>

              <li className="preview-welcome-card">
                <div className="preview-welcome-card-icon" aria-hidden="true">
                  <CircleHelp size={18} />
                </div>
                <div className="preview-welcome-card-body">
                  <h4>¿Dudas en alguna pantalla?</h4>
                  <p>
                    Pulsa el icono de ayuda{' '}
                    <span aria-hidden="true">(?)</span> arriba a la derecha en
                    cualquier pantalla y verás una explicación de lo que hace y
                    cómo sacarle partido.
                  </p>
                </div>
              </li>

              <li className="preview-welcome-card preview-welcome-card--accent">
                <div className="preview-welcome-card-icon" aria-hidden="true">
                  <Sparkles size={18} />
                </div>
                <div className="preview-welcome-card-body">
                  <h4>Reclámalo cuando quieras</h4>
                  <p>
                    Indícanos tu email y arrancamos tu{' '}
                    <strong>prueba gratuita de {TRIAL_DAYS} días</strong>.
                    Sin tarjeta, sin permanencia, sin sorpresas — empezarás
                    con tu club en blanco para construirlo a tu medida.
                  </p>
                </div>
              </li>

              <li className="preview-welcome-card">
                <div className="preview-welcome-card-icon" aria-hidden="true">
                  <Trash2 size={18} />
                </div>
                <div className="preview-welcome-card-body">
                  <h4>¿No te interesa? No hace falta hacer nada</h4>
                  <p>
                    Si nadie reclama el club en {PREVIEW_WINDOW_DAYS} días, se
                    borra automáticamente. Tus datos reales nunca estuvieron
                    aquí.
                  </p>
                </div>
              </li>
            </ul>
          </div>
          <div className="modal-foot">
            <AlertDialog.Cancel asChild>
              <Button variant="ghost" size="sm" icon={Eye}>
                Explorar primero
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <Button
                variant="primary"
                size="sm"
                icon={Sparkles}
                onClick={goToClaim}
                className="preview-allowed"
              >
                Reclamar mi club
              </Button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
